import type { SimulationPack, SimulationScenario } from '@/hooks/use-platform-simulation'

export type DataBenchCopy = {
  pageTitle: string
  pageDescription: string
  subjectNoun: string
  poolNoun: string
  artifactNoun: string
  emptyMessage: string
  supportsHire: boolean
  supportsCv: boolean
  supportsDemo: boolean
  isEmployment: boolean
}

export function dataBenchCopy(pack: SimulationPack | undefined): DataBenchCopy {
  return {
    pageTitle: pack?.page_title || 'Simulation',
    pageDescription:
      pack?.page_description ||
      'See if scoring picks the people we expect. Open a sample job, score the CVs, then take what you learn to Rubrics. This does not change live hiring.',
    subjectNoun: pack?.subject_noun || 'job',
    poolNoun: pack?.pool_noun || 'candidates',
    artifactNoun: pack?.artifact_noun || 'job description',
    emptyMessage: pack?.empty_pack_message || 'No data-bench packs for this tenant yet.',
    supportsHire: pack?.supports_hire_events !== false,
    supportsCv: pack?.supports_cv_upload !== false,
    supportsDemo: pack?.supports_demo_pack !== false,
    isEmployment: (pack?.methodology_id || 'employment_match') === 'employment_match',
  }
}

export function isProductWorldScenario(scenario: SimulationScenario): boolean {
  const meta = scenario.metadata || {}
  return Boolean(meta.alpha_pack || meta.pack === 'alpha_world')
}

/** Where a sample job came from, in words an operator can read. */
export function sampleJobSourceLabel(scenario: SimulationScenario): string {
  if (isProductWorldScenario(scenario)) return 'Live product'
  const meta = scenario.metadata || {}
  if (meta.demo_pack || meta.pack === 'condition_demo_pack') return 'Generated'
  if (meta.pack === 'haystack' || meta.haystack_test_id) return 'Ready-made'
  return 'Built by hand'
}

export function sampleJobStatusBadge(status: string): {
  label: string
  variant: 'secondary' | 'successSoft'
} {
  if (status === 'scored') return { label: 'Scored', variant: 'successSoft' }
  if (status === 'draft') return { label: 'Draft', variant: 'secondary' }
  return { label: 'Not scored yet', variant: 'secondary' }
}

export function personCount(scenario: SimulationScenario): number {
  return scenario.profile_ids?.length || scenario.profiles?.length || 0
}

/** Visible miss when a scoring run has no required chips. */
export function opportunityRequiredSkillsMiss(scenario: SimulationScenario): string | null {
  const required = (scenario.opportunity_skills ?? []).filter(
    (row) => row.requirement_type === 'required' && Boolean(row.skill_name?.trim()),
  )
  if (required.length > 0) return null
  const misses =
    scenario.completeness_log?.opportunity?.misses ??
    scenario.completeness_log?.opening?.misses ??
    []
  const generated = sampleJobSourceLabel(scenario) === 'Generated'
  if (!generated && !misses.includes('required_skills')) return null
  return 'Required skills are missing. Scoring will not match chips until you add them or drop a job file.'
}
